import { Navigate, Outlet, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BookSpinner from "@/components/common/BookSpinner";

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

/**
 * Route guard that ensures only authenticated users can access the wrapped
 * content.  While the initial auth check is still running it renders a
 * loading spinner; once the check completes it either renders the children
 * or redirects the user to the login page.
 *
 * When used as a layout route (no children), it renders `<Outlet />` so that
 * nested child routes are displayed.  When wrapping explicit children, it
 * renders those children directly.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || "fr";

  // Still checking auth state — show spinner
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <BookSpinner size="lg" />
      </div>
    );
  }

  // Not authenticated — redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to={`/${currentLang}/login`} replace />;
  }

  // Authenticated — render children or nested routes via Outlet
  return <>{children ?? <Outlet />}</>;
}
